import { type FC, type ReactNode, useEffect } from "react";

import { PublicFooter } from "./PublicFooter";
import { type PublicHeaderNavItem, PublicHeader } from "./PublicHeader";

interface PublicPageShellProps {
  children: ReactNode;
  navItems: readonly PublicHeaderNavItem[];
  primaryActionHref: string;
  primaryActionLabel: string;
}

/**
 * The page renders after the browser's own fragment scroll, so a link such as
 * `/#models` from another page would land at the top. Redo it once mounted.
 */
function useScrollToLocationHash(): void {
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const id = window.location.hash.slice(1);
      if (id) {
        document.getElementById(id)?.scrollIntoView();
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, []);
}

export const PublicPageShell: FC<PublicPageShellProps> = ({
  children,
  navItems,
  primaryActionHref,
  primaryActionLabel,
}) => {
  useScrollToLocationHash();
  return (
    <div className="relative min-h-screen overflow-x-clip bg-[#0a1020] text-slate-100 antialiased">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(1400px_900px_at_50%_0%,#111a30_0%,#0c1426_55%,#0a1020_100%)]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.055)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.055)_1px,transparent_1px)] bg-[size:56px_56px]"
        aria-hidden="true"
      />
      <div className="relative">
        <PublicHeader
          navItems={navItems}
          primaryActionHref={primaryActionHref}
          primaryActionLabel={primaryActionLabel}
        />
        <main>{children}</main>
        <PublicFooter />
      </div>
    </div>
  );
};
